/**
 * Touch Panel assignment service (Phase 4).
 * Collection: tenants/{tenantId}/touchPanelAssignments/{deviceId}
 * Doc id === deviceId enforces one active assignment per physical device.
 *
 * Eligible devices are Edge Agents (not Algo/phone inventory devices).
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  where,
} from 'firebase/firestore';
import { db } from './firebase';
import { fetchAgents } from '../api/agentApi';
import { getTouchPanel } from './touchPanelService';
import { createLocalId } from '../pages/Admin/TouchPanelDesigner/panelModel';
import {
  toEligibleDeviceView,
  buildDeviceSnapshot,
  getTouchPanelCompatibility,
  extractDeviceId,
  isTouchPanelCandidate,
  filterEligibleDevices,
} from './touchPanelDeployable/eligibility';
import { computeAssignmentStatus } from './touchPanelDeployable/assignmentStatus';

export const TOUCH_PANEL_ASSIGNMENTS_COLLECTION = 'touchPanelAssignments';

/**
 * Backend-owned delivery / Agent-result fields.
 * Clients must not write these (except clearing to null on reassignment).
 * Admin SDK / delivery pipeline owns success, hash, and timestamps.
 */
export const TOUCH_PANEL_ASSIGNMENT_BACKEND_OWNED_FIELDS = Object.freeze([
  'deployedPanelRevision',
  'lastDeploymentId',
  'lastDeploymentStatus',
  'lastDeploymentAt',
  'lastDeploymentError',
  'activePanelHash',
  'agentResult',
  'acknowledgedAt',
  'completedAt',
  'publishedAt',
]);

/**
 * Strip backend-owned fields from a client write payload.
 * When preserveFrom is provided, existing backend values are copied through
 * so merge:false / full-doc writes cannot forge or wipe delivery success.
 * Pass preserveFrom=null to omit fields entirely (create / clear on panel change).
 */
export function omitBackendOwnedAssignmentFields(payload, { preserveFrom = undefined } = {}) {
  const next = { ...(payload || {}) };
  TOUCH_PANEL_ASSIGNMENT_BACKEND_OWNED_FIELDS.forEach((field) => {
    delete next[field];
    if (preserveFrom && Object.prototype.hasOwnProperty.call(preserveFrom, field)) {
      const value = preserveFrom[field];
      if (value !== undefined) {
        next[field] = value;
      }
    }
  });
  return next;
}

function ensureTenantId(tenantId) {
  if (!tenantId || !String(tenantId).trim()) {
    throw new Error('tenantId is required');
  }
  return String(tenantId).trim();
}

function nowIso() {
  return new Date().toISOString();
}

function assignmentsCollection(tenantId) {
  return collection(
    db,
    'tenants',
    ensureTenantId(tenantId),
    TOUCH_PANEL_ASSIGNMENTS_COLLECTION
  );
}

function assignmentDocRef(tenantId, deviceId) {
  if (!deviceId) throw new Error('deviceId is required');
  return doc(
    db,
    'tenants',
    ensureTenantId(tenantId),
    TOUCH_PANEL_ASSIGNMENTS_COLLECTION,
    deviceId
  );
}

export function mapAssignmentDoc(snapshot) {
  if (!snapshot?.exists()) return null;
  const data = snapshot.data() || {};
  return {
    id: snapshot.id,
    tenantId: data.tenantId || null,
    panelId: data.panelId || null,
    deviceId: data.deviceId || snapshot.id,
    createdAt: data.createdAt || null,
    createdBy: data.createdBy || null,
    updatedAt: data.updatedAt || null,
    updatedBy: data.updatedBy || null,
    enabled: data.enabled !== false,
    assignedPanelRevision:
      data.assignedPanelRevision == null ? null : Number(data.assignedPanelRevision),
    preparedPanelRevision:
      data.preparedPanelRevision == null ? null : Number(data.preparedPanelRevision),
    status: data.status || 'assigned',
    deviceSnapshot: data.deviceSnapshot || null,
    lastPreparedAt: data.lastPreparedAt || null,
    lastPreparedBy: data.lastPreparedBy || null,
    lastDeploymentId: data.lastDeploymentId || null,
    lastDeploymentStatus: data.lastDeploymentStatus || null,
    lastDeploymentError: data.lastDeploymentError || null,
    lastDeploymentAt: data.lastDeploymentAt || null,
    deployedPanelRevision:
      data.deployedPanelRevision == null ? null : Number(data.deployedPanelRevision),
    activePanelHash: data.activePanelHash || null,
  };
}

export async function listTouchPanelAssignments(tenantId) {
  const snap = await getDocs(assignmentsCollection(tenantId));
  return snap.docs.map((d) => mapAssignmentDoc(d)).filter(Boolean);
}

export async function listAssignmentsForPanel(tenantId, panelId) {
  const snap = await getDocs(
    query(assignmentsCollection(tenantId), where('panelId', '==', panelId))
  );
  return snap.docs
    .map((d) => mapAssignmentDoc(d))
    .filter((a) => a && a.enabled !== false);
}

export async function getAssignmentForDevice(tenantId, deviceId) {
  const snap = await getDoc(assignmentDocRef(tenantId, deviceId));
  return mapAssignmentDoc(snap);
}

export async function getTouchPanelAssignment(tenantId, assignmentId) {
  const snap = await getDoc(assignmentDocRef(tenantId, assignmentId));
  return mapAssignmentDoc(snap);
}

async function loadTenantAgents(tenantId) {
  // AgentListSchema caps limit at 100
  const response = await fetchAgents({ tenant_id: tenantId, tenantId, limit: 100 });
  const agents =
    response?.data?.agents ||
    response?.agents ||
    response?.data?.items ||
    response?.items ||
    [];
  return Array.isArray(agents) ? agents : [];
}

/**
 * List eligible Touch Panel devices for a tenant (candidates + current assignments).
 * Includes incompatible agents only when includeIncompatible=true.
 */
export async function listEligibleTouchPanelDevices(
  tenantId,
  { panelId = null, includeIncompatible = true, search = '' } = {}
) {
  const tid = ensureTenantId(tenantId);
  const [agents, assignments, panelsHint] = await Promise.all([
    loadTenantAgents(tid),
    listTouchPanelAssignments(tid),
    // panel names for assignment labels — lightweight map from assignment panelIds only
    Promise.resolve(null),
  ]);
  void panelsHint;

  const assignmentByDevice = new Map();
  assignments.forEach((a) => {
    if (a?.deviceId) assignmentByDevice.set(a.deviceId, a);
  });

  const panelNameById = {};
  // Enrich names lazily from assignment snapshots only
  assignments.forEach((a) => {
    if (a.panelId && a.deviceSnapshot?.assignedPanelName) {
      panelNameById[a.panelId] = a.deviceSnapshot.assignedPanelName;
    }
  });

  let devices = agents
    .filter((a) => includeIncompatible || isTouchPanelCandidate(a))
    .map((agent) => {
      const deviceId = extractDeviceId(agent);
      const assignment = deviceId ? assignmentByDevice.get(deviceId) : null;
      return toEligibleDeviceView(agent, { assignment, panelNameById });
    })
    .filter((d) => d.deviceId);

  // Prefer candidates first
  devices.sort((a, b) => {
    const score = (d) =>
      d.compatibilityStatus === 'compatible'
        ? 0
        : d.compatibilityStatus === 'unknown'
          ? 1
          : 2;
    return score(a) - score(b) || String(a.name).localeCompare(String(b.name));
  });

  if (panelId) {
    // no-op filter reserved for future "already on this panel" views
  }

  return filterEligibleDevices(devices, search);
}

/**
 * Assign panel → device. Rejects incompatible unless allowIncompatible.
 * Rejects duplicate active assignment unless replaceExisting.
 */
export async function assignPanelToDevice(
  tenantId,
  { panelId, deviceId, userId, deviceView = null, allowIncompatible = false, replaceExisting = false } = {}
) {
  const tid = ensureTenantId(tenantId);
  if (!panelId) throw new Error('panelId is required');
  if (!deviceId) throw new Error('deviceId is required');

  const panel = await getTouchPanel(tid, panelId);
  if (!panel) throw new Error('Touch panel not found');

  let view = deviceView;
  if (!view) {
    const devices = await listEligibleTouchPanelDevices(tid, { includeIncompatible: true });
    view = devices.find((d) => d.deviceId === deviceId);
  }
  if (!view) {
    throw new Error('Device not found in tenant agent list');
  }

  const compatibility =
    view.compatibilityStatus || getTouchPanelCompatibility(view.raw || view);
  if (compatibility === 'incompatible' && !allowIncompatible) {
    const err = new Error('Device is incompatible with Touch Panels');
    err.code = 'DEVICE_INCOMPATIBLE';
    throw err;
  }

  const existing = await getAssignmentForDevice(tid, deviceId);
  if (existing && existing.enabled !== false && existing.panelId !== panelId) {
    if (!replaceExisting) {
      const err = new Error(
        `Device already assigned to panel ${existing.panelId}. Confirm replace to continue.`
      );
      err.code = 'DEVICE_ALREADY_ASSIGNED';
      err.existingAssignment = existing;
      throw err;
    }
  }

  const iso = nowIso();
  const snapshot = buildDeviceSnapshot(view);
  const samePanel = existing?.panelId === panelId;
  let payload = {
    id: deviceId,
    tenantId: tid,
    panelId,
    deviceId,
    createdAt: existing?.createdAt || iso,
    createdBy: existing?.createdBy || userId || null,
    updatedAt: iso,
    updatedBy: userId || null,
    enabled: true,
    assignedPanelRevision: Number(panel.revision) || 1,
    preparedPanelRevision: samePanel ? existing.preparedPanelRevision ?? null : null,
    status: 'assigned',
    deviceSnapshot: snapshot,
    lastPreparedAt: samePanel ? existing.lastPreparedAt ?? null : null,
    lastPreparedBy: samePanel ? existing.lastPreparedBy ?? null : null,
  };

  // Same panel: preserve backend-owned delivery fields.
  // Panel change / new assign: omit them (null on create — no forged success).
  payload = omitBackendOwnedAssignmentFields(payload, {
    preserveFrom: samePanel ? existing : null,
  });

  await setDoc(assignmentDocRef(tid, deviceId), payload, { merge: false });
  return { ...payload };
}

export async function unassignDevice(tenantId, deviceId, userId) {
  const tid = ensureTenantId(tenantId);
  const existing = await getAssignmentForDevice(tid, deviceId);
  if (!existing) return null;

  // Hard delete keeps uniqueness clean; no silently broken docs
  await deleteDoc(assignmentDocRef(tid, deviceId));
  return { ...existing, enabled: false, updatedAt: nowIso(), updatedBy: userId || null };
}

/**
 * Mark assignments prepared for a panel revision after deployment prep.
 */
export async function markAssignmentsPrepared(
  tenantId,
  { panelId, panelRevision, deviceIds, deploymentId, userId } = {}
) {
  const tid = ensureTenantId(tenantId);
  const iso = nowIso();
  const results = [];

  for (const deviceId of deviceIds || []) {
    const existing = await getAssignmentForDevice(tid, deviceId);
    if (!existing || existing.panelId !== panelId || existing.enabled === false) {
      continue;
    }
    // Client prepare path: only prepare metadata. Never write deployed /
    // lastDeployment* success fields — backend owns those after MQTT delivery.
    void deploymentId;
    const next = omitBackendOwnedAssignmentFields(
      {
        ...existing,
        preparedPanelRevision: Number(panelRevision),
        assignedPanelRevision: Number(panelRevision),
        status: 'prepared',
        lastPreparedAt: iso,
        lastPreparedBy: userId || null,
        updatedAt: iso,
        updatedBy: userId || null,
      },
      { preserveFrom: existing }
    );
    await setDoc(assignmentDocRef(tid, deviceId), next, { merge: false });
    results.push(next);
  }
  return results;
}

export function getAssignmentStatusForPanel(assignment, panelRevision, deviceCompatibility) {
  return computeAssignmentStatus({
    assignment,
    panelRevision,
    deviceCompatibility,
  });
}

/** Used by tests — expose id helper without leaking createLocalId elsewhere */
export function createAssignmentId(deviceId) {
  return deviceId || createLocalId('assign');
}

export { filterEligibleDevices };
